import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface Product {
  id: string;
  name: string;
  product_code: string;
  barcode: string;
  brand: string;
  model: string;
  category_id: string;
  sale_price: number;
  purchase_price: number;
  stock: number;
  shelf_location: string;
}

interface CartItem {
  product: Product;
  quantity: number;
  unitPrice: number;
}

type PaymentMethod = "nakit" | "kart" | "iban" | "sanal_pos";

const basePaymentOptions: { id: PaymentMethod; label: string; icon: string }[] = [
  { id: "nakit",     label: "Nakit",         icon: "ri-money-dollar-circle-line" },
  { id: "kart",      label: "Kredi Kartı",   icon: "ri-bank-card-line"           },
  { id: "iban",      label: "IBAN/Havale",   icon: "ri-exchange-dollar-line"     },
  { id: "sanal_pos", label: "Sanal POS",     icon: "ri-secure-payment-line"      },
];

interface PaymentConfig {
  active_provider: "none" | "iyzico" | "paytr";
  provider_label: string;
}

export default function WarehousePosPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId || user?.id || "";

  // Products & search
  const [products, setProducts]     = useState<Product[]>([]);
  const [query, setQuery]           = useState("");
  const [searching, setSearching]   = useState(false);
  const [results, setResults]       = useState<Product[]>([]);
  const [loadingProds, setLoadingProds] = useState(true);

  // Cart
  const [cart, setCart]             = useState<CartItem[]>([]);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>({ active_provider: "none", provider_label: "" });

  // Fetch active virtual POS provider
  const loadPaymentConfig = useCallback(async () => {
    if (!dealerId) return;
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("dealer_payment_settings")
        .select("active_provider")
        .eq("dealer_id", dealerId)
        .maybeSingle();
      if (data?.active_provider && data.active_provider !== "none") {
        const label = data.active_provider === "iyzico" ? "İyzico" : "PayTR";
        setPaymentConfig({ active_provider: data.active_provider, provider_label: label });
      }
    } catch {
      // table may not exist yet
    }
  }, [dealerId]);

  // Checkout
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [customerName, setCustomerName]   = useState("");
  const [customerNote, setCustomerNote]   = useState("");
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState("");
  const [saleComplete, setSaleComplete]   = useState(false);
  const [lastSaleId, setLastSaleId]       = useState<string | null>(null);

  const barcodeRef = useRef<HTMLInputElement>(null);
  const searchRef  = useRef<HTMLInputElement>(null);

  // ── Load products ───────────────────────────────────────────────────────────
  const loadProducts = useCallback(async () => {
    if (!dealerId) return;
    setLoadingProds(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("products")
        .select("id, name, product_code, barcode, brand, model, category_id, sale_price, purchase_price, stock, shelf_location")
        .eq("dealer_id", dealerId)
        .order("name");
      setProducts((data as Product[]) || []);
    } finally {
      setLoadingProds(false);
    }
  }, [dealerId]);

  useEffect(() => { loadProducts(); loadPaymentConfig(); }, [loadProducts, loadPaymentConfig]);

  // ── Search ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const matches = products.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.product_code?.toLowerCase().includes(q) ||
        p.barcode?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.model?.toLowerCase().includes(q)
    );
    setResults(matches);
    setSearching(false);
  }, [query, products]);

  // ── Barcode scan ───────────────────────────────────────────────────────────
  const handleBarcodeEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    const val = query.trim();
    if (!val) return;
    const exact = products.find(
      (p) => p.barcode === val || p.product_code === val
    );
    if (exact) {
      addToCart(exact);
      setQuery("");
      setResults([]);
    }
  };

  // ── Cart ───────────────────────────────────────────────────────────────────
  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { product, quantity: 1, unitPrice: product.sale_price }];
    });
    setQuery("");
    setResults([]);
    barcodeRef.current?.focus();
  };

  const updateQty = (id: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) => i.product.id === id ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0)
    );
  };

  const updatePrice = (id: string, val: string) => {
    const n = parseFloat(val);
    if (isNaN(n) || n < 0) return;
    setCart((prev) => prev.map((i) => i.product.id === id ? { ...i, unitPrice: n } : i));
  };

  const removeFromCart = (id: string) => setCart((prev) => prev.filter((i) => i.product.id !== id));

  const cartTotal = cart.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  // ── Checkout ───────────────────────────────────────────────────────────────
  const openCheckout = () => {
    if (!cart.length) return;
    setPaymentMethod(null);
    setCustomerName("");
    setCustomerNote("");
    setSaveError("");
    setSaleComplete(false);
    setShowCheckout(true);
  };

  const confirmSale = async () => {
    if (!paymentMethod) { setSaveError("Lütfen ödeme yöntemi seçin!"); return; }
    setSaving(true);
    setSaveError("");
    try {
      const supabase = getSupabase();
      const { data: saleData, error: saleErr } = await supabase
        .from("warehouse_sales")
        .insert({
          dealer_id:      dealerId,
          customer_name:  customerName || null,
          note:           customerNote || null,
          payment_method: paymentMethod,
          total_amount:   cartTotal,
          staff_id:       user?.id || null,
          sale_type:      "warehouse",
        })
        .select("id")
        .single();

      if (saleErr) throw saleErr;

      const items = cart.map((i) => ({
        sale_id:      saleData.id,
        product_id:   i.product.id,
        quantity:     i.quantity,
        unit_price:   i.unitPrice,
        purchase_price: i.product.purchase_price,
        description:  i.product.name,
      }));

      const { error: itemsErr } = await supabase.from("warehouse_sale_items").insert(items);
      if (itemsErr) throw itemsErr;

      // Decrement stock
      for (const item of cart) {
        const newStock = Math.max(0, (item.product.stock || 0) - item.quantity);
        await supabase.from("products").update({ stock: newStock }).eq("id", item.product.id);
      }

      setLastSaleId(saleData.id);
      setSaleComplete(true);
      logAction(
        dealerId,
        user?.id || null,
        LOG_ACTIONS.SALE_CREATE,
        `Depo Satışı · ${basePaymentOptions.find((p) => p.id === paymentMethod)?.label} · ${cart.length} kalem · ₺${cartTotal.toFixed(2)}${customerName ? ` · Müşteri: ${customerName}` : ""}`
      );
      // Refresh product list for updated stocks
      loadProducts();
    } catch (e: any) {
      setSaveError(e.message || "Satış kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const resetSale = () => {
    setCart([]);
    setShowCheckout(false);
    setSaleComplete(false);
    setPaymentMethod(null);
    setCustomerName("");
    setCustomerNote("");
    setSaveError("");
    setLastSaleId(null);
    setQuery("");
    setTimeout(() => barcodeRef.current?.focus(), 100);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full">

      {/* ── Left: Search + Products ── */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h4 className="text-lg font-semibold text-foreground-950 mb-1">Depo Satış Noktası</h4>
            <p className="text-xs text-foreground-500">Barkod okuyun veya ürün arayın</p>
          </div>
          <a
            href="/warehouse/model-control"
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-100 hover:bg-amber-200/70 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap shrink-0"
          >
            <i className="ri-list-settings-line"></i> Model Kontrol
          </a>
        </div>

        {/* Search / Barcode input */}
        <div className="relative mb-4">
          <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
          <input
            ref={barcodeRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleBarcodeEnter}
            placeholder="Barkod okutun veya ürün adı / kodu arayın..."
            autoFocus
            className="w-full pl-10 pr-4 py-3 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          {query && (
            <button onClick={() => { setQuery(""); setResults([]); barcodeRef.current?.focus(); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>

        {/* Search results */}
        {results.length > 0 && (
          <div className="bg-background-50 border border-background-200/70 rounded-xl mb-4 max-h-64 overflow-y-auto divide-y divide-background-200/70">
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-background-100/60 transition-colors cursor-pointer text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-box-3-line text-primary-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-950 truncate">{p.name}</p>
                  <p className="text-xs text-foreground-400">{p.product_code || p.barcode || "—"} {p.brand ? `· ${p.brand}` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-foreground-950">₺{Number(p.sale_price).toFixed(2)}</p>
                  <p className={`text-xs ${p.stock > 0 ? "text-emerald-600" : "text-accent-600"}`}>
                    {p.stock > 0 ? `${p.stock} adet` : "Stok yok"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {query && results.length === 0 && !searching && (
          <div className="bg-background-50 border border-background-200/70 rounded-xl px-4 py-6 text-center mb-4">
            <i className="ri-search-line text-2xl text-foreground-300 block mb-2"></i>
            <p className="text-sm text-foreground-400">"{query}" için sonuç bulunamadı</p>
          </div>
        )}

        {/* All products grid (when no search) */}
        {!query && (
          <div className="flex-1 overflow-y-auto">
            {loadingProds ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              </div>
            ) : products.length === 0 ? (
              <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
                <i className="ri-box-3-line text-3xl text-foreground-300 block mb-2"></i>
                <p className="text-sm text-foreground-400">Henüz ürün eklenmemiş</p>
                <p className="text-xs text-foreground-400 mt-1">Stok yönetiminden veya Excel içe aktarımından ürün ekleyin</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2">
                {products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addToCart(p)}
                    disabled={p.stock <= 0}
                    className={`bg-background-50 border rounded-xl p-3 text-left transition-all cursor-pointer group ${
                      p.stock > 0
                        ? "border-background-200/70 hover:border-primary-300 hover:shadow-sm hover:bg-primary-50/30"
                        : "border-background-200/40 opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-100 group-hover:bg-primary-100 flex items-center justify-center mb-2 transition-colors">
                      <i className="ri-box-3-line text-foreground-400 group-hover:text-primary-600 text-sm transition-colors"></i>
                    </div>
                    <p className="text-xs font-semibold text-foreground-950 line-clamp-2 mb-1">{p.name}</p>
                    {p.product_code && <p className="text-[10px] text-foreground-400 mb-1 font-mono">{p.product_code}</p>}
                    <p className="text-sm font-bold text-primary-600">₺{Number(p.sale_price).toFixed(2)}</p>
                    <p className={`text-[10px] mt-0.5 ${p.stock > 0 ? "text-emerald-600" : "text-accent-500"}`}>
                      {p.stock > 0 ? `${p.stock} stok` : "Stok yok"}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Right: Cart ── */}
      <div className="w-full lg:w-80 xl:w-96 flex flex-col bg-background-50 border border-background-200/70 rounded-xl overflow-hidden shrink-0">
        {/* Cart header */}
        <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
          <div>
            <h5 className="font-semibold text-foreground-950">Sepet</h5>
            <p className="text-xs text-foreground-400">{cartCount} ürün</p>
          </div>
          {cart.length > 0 && (
            <button onClick={() => setCart([])} className="text-xs text-accent-600 hover:text-accent-800 cursor-pointer flex items-center gap-1">
              <i className="ri-delete-bin-line"></i> Temizle
            </button>
          )}
        </div>

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto divide-y divide-background-200/70">
          {cart.length === 0 ? (
            <div className="py-16 text-center">
              <i className="ri-shopping-cart-line text-3xl text-foreground-300 block mb-2"></i>
              <p className="text-sm text-foreground-400">Sepet boş</p>
              <p className="text-xs text-foreground-400 mt-1">Ürün eklemek için tıklayın</p>
            </div>
          ) : (
            cart.map((item) => (
              <div key={item.product.id} className="px-4 py-3">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm font-medium text-foreground-950 flex-1 pr-2 leading-tight">{item.product.name}</p>
                  <button onClick={() => removeFromCart(item.product.id)} className="text-foreground-300 hover:text-accent-600 cursor-pointer shrink-0">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(item.product.id, -1)} className="w-7 h-7 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-600 hover:bg-background-200/70 cursor-pointer text-sm">
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-8 text-center text-sm font-semibold text-foreground-950">{item.quantity}</span>
                    <button onClick={() => updateQty(item.product.id, 1)} className="w-7 h-7 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-600 hover:bg-background-200/70 cursor-pointer text-sm">
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-foreground-400">₺</span>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => updatePrice(item.product.id, e.target.value)}
                      className="w-20 px-2 py-1 bg-background-100 border border-background-200/70 rounded-lg text-sm text-right font-semibold text-foreground-950 focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                </div>
                <p className="text-xs text-foreground-400 text-right mt-1">
                  Toplam: ₺{(item.quantity * item.unitPrice).toFixed(2)}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Cart footer */}
        <div className="border-t border-background-200/70 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-foreground-600">Genel Toplam</span>
            <span className="text-xl font-bold text-foreground-950">₺{cartTotal.toFixed(2)}</span>
          </div>
          <button
            onClick={openCheckout}
            disabled={cart.length === 0}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <i className="ri-secure-payment-line"></i> Ödeme Al
          </button>
        </div>
      </div>

      {/* ── Checkout Modal ── */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => !saleComplete && setShowCheckout(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-md mx-0 lg:mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>

            {saleComplete ? (
              /* ── Sale complete ── */
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-3xl text-emerald-500"></i>
                </div>
                <h4 className="text-lg font-bold text-foreground-950 mb-1">Satış Tamamlandı!</h4>
                <p className="text-sm text-foreground-500 mb-1">
                  {basePaymentOptions.find((p) => p.id === paymentMethod)?.label} · ₺{cartTotal.toFixed(2)}
                </p>
                {customerName && <p className="text-sm text-foreground-400 mb-4">Müşteri: {customerName}</p>}
                <button
                  onClick={resetSale}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold cursor-pointer"
                >
                  <i className="ri-add-line mr-1"></i> Yeni Satış
                </button>
              </div>
            ) : (
              /* ── Payment selection ── */
              <>
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-lg font-semibold text-foreground-950">Ödeme Al</h4>
                  <button onClick={() => setShowCheckout(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {/* Order summary */}
                <div className="bg-background-100 rounded-xl p-3 mb-4 text-xs text-foreground-500 space-y-1">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex justify-between">
                      <span>{item.quantity}× {item.product.name}</span>
                      <span className="font-medium text-foreground-700">₺{(item.quantity * item.unitPrice).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-background-200/70 pt-1.5 mt-1.5 flex justify-between font-bold text-foreground-950 text-sm">
                    <span>Toplam</span>
                    <span>₺{cartTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Customer name */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Müşteri Adı <span className="text-foreground-300">(opsiyonel)</span></label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Müşteri adı / firma"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Note */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Not <span className="text-foreground-300">(opsiyonel)</span></label>
                  <input
                    type="text"
                    value={customerNote}
                    onChange={(e) => setCustomerNote(e.target.value)}
                    placeholder="Sipariş notu"
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Payment methods */}
                <p className="text-xs font-medium text-foreground-500 mb-2">Ödeme Yöntemi *</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {basePaymentOptions
                    .filter((opt) => opt.id !== "sanal_pos" || paymentConfig.active_provider !== "none")
                    .map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => { setPaymentMethod(opt.id); setSaveError(""); }}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl text-xs font-medium border-2 transition-all cursor-pointer ${
                          paymentMethod === opt.id
                            ? "border-primary-500 bg-primary-50 text-primary-700"
                            : "border-background-200/70 bg-background-100 text-foreground-600 hover:border-primary-300"
                        }`}
                      >
                        <i className={`${opt.icon} text-xl`}></i>
                        {opt.id === "sanal_pos"
                          ? `Sanal POS (${paymentConfig.provider_label})`
                          : opt.label}
                      </button>
                    ))}
                </div>

                {/* Virtual POS info box */}
                {paymentMethod === "sanal_pos" && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700 flex items-start gap-2">
                    <i className="ri-information-line shrink-0 mt-0.5"></i>
                    <span>
                      <strong>{paymentConfig.provider_label}</strong> üzerinden ödeme alınacak.
                      Müşteriye ödeme linki / formu yönlendirilecektir.
                      API entegrasyonu için Bayi Admin → Sanal POS Ayarları sayfasından yapılandırma yapın.
                    </span>
                  </div>
                )}

                {saveError && (
                  <div className="mb-4 p-3 bg-accent-50 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                    <i className="ri-error-warning-line"></i> {saveError}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-background-200/70"
                  >
                    İptal
                  </button>
                  <button
                    onClick={confirmSale}
                    disabled={saving || !paymentMethod}
                    className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-xl text-sm font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <i className="ri-loader-4-line animate-spin"></i> Kaydediliyor...
                      </span>
                    ) : (
                      <><i className="ri-check-line mr-1"></i> Satışı Onayla</>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
